/**
 * Timezone utilities for handling Greece (Athens) timezone
 * Corfu follows Greece timezone: EET (UTC+2) in winter, EEST (UTC+3) in summer
 */

/**
 * Convert a UTC datetime to Greece local time
 * @param {Date|string} utcDate - UTC date object or ISO string
 * @returns {Date} Date object representing the local Greece time
 */
export const utcToGreeceTime = (utcDate) => {
  const date = new Date(utcDate);
  
  // Check if we're in daylight saving time (last Sunday in March to last Sunday in October)
  const year = date.getFullYear();
  const marchLastSunday = new Date(year, 2, 31 - new Date(year, 2, 31).getDay());
  const octoberLastSunday = new Date(year, 9, 31 - new Date(year, 9, 31).getDay());
  
  const isDST = date >= marchLastSunday && date < octoberLastSunday;
  const offsetHours = isDST ? 3 : 2; // EEST (UTC+3) or EET (UTC+2)
  
  // Add the offset to get local Greece time
  return new Date(date.getTime() + (offsetHours * 60 * 60 * 1000));
};

/**
 * Convert a Greece local time to UTC
 * @param {Date|string} localDate - Local Greece date object or string
 * @returns {Date} Date object representing the UTC time
 */
export const greeceTimeToUtc = (localDate) => {
  const date = typeof localDate === 'string' ? new Date(localDate) : localDate;
  
  // Check if we're in daylight saving time (last Sunday in March to last Sunday in October)
  const year = date.getFullYear();
  const marchLastSunday = new Date(year, 2, 31 - new Date(year, 2, 31).getDay());
  const octoberLastSunday = new Date(year, 9, 31 - new Date(year, 9, 31).getDay());
  
  const isDST = date >= marchLastSunday && date < octoberLastSunday;
  const offsetHours = isDST ? 3 : 2; // EEST (UTC+3) or EET (UTC+2)
  
  // Subtract the offset to get UTC time
  return new Date(date.getTime() - (offsetHours * 60 * 60 * 1000));
};

/**
 * Format a datetime string to preserve local Greece time
 * @param {Date} date - Date object
 * @returns {string} ISO-like string representing local time (YYYY-MM-DDTHH:mm:ss)
 */
export const formatGreeceLocalTime = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

/**
 * Format a datetime for display in Greece timezone
 * @param {Date|string} date - Date object or ISO string
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatGreeceDateTime = (date, options = {}) => {
  const greeceDate = typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/) 
    ? new Date(date) // Already local time
    : utcToGreeceTime(date); // Convert from UTC
  
  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  };
  
  return greeceDate.toLocaleString('en-GB', { ...defaultOptions, ...options });
};

/**
 * Get the current Greece timezone offset
 * @returns {Object} Object with offset info
 */
export const getGreeceTimezoneInfo = () => {
  const now = new Date();
  const year = now.getFullYear();
  const marchLastSunday = new Date(year, 2, 31 - new Date(year, 2, 31).getDay());
  const octoberLastSunday = new Date(year, 9, 31 - new Date(year, 9, 31).getDay());
  
  const isDST = now >= marchLastSunday && now < octoberLastSunday;
  
  return {
    isDST,
    offsetHours: isDST ? 3 : 2,
    zoneName: isDST ? 'EEST' : 'EET',
    displayName: isDST ? 'Eastern European Summer Time (UTC+3)' : 'Eastern European Time (UTC+2)'
  };
}; 